import { RequireAuthAction } from "@/components/auth/RequireAuthAction";
import { TokenAmount, TokenLabel } from "@/components/brand/TokenAmount";
import { TokenIcon } from "@/components/brand/TokenIcon";
import { StepIndicator } from "@/components/common/StepIndicator";
import { ConfirmSheet } from "@/components/defi/ConfirmSheet";
import { RiskGauge } from "@/components/defi/RiskGauge";
import {
	runDemoUpdateStages,
	UpdateStageLoader,
	type UpdateStage,
} from "@/components/defi/UpdateStageLoader";
import { Button } from "@/components/ui/Button";
import { RangeSlider } from "@/components/ui/RangeSlider";
import { type CollateralKey, FIXTURE } from "@/data/fixtures";
import {
	useGetPublicPriceFeeds,
	useProtocolRiskParams,
	toRiskParams,
} from "@/hooks/usePublicProtocol";
import { useAuth } from "@/lib/auth";
import {
	depositableTokenMax,
	mintableUsdmMax,
} from "@/lib/amount-limits";
import {
	liquidationPrice,
	maxLtvFromCr,
} from "@/lib/defi-math";
import { ensureProtocolUser } from "@/lib/ensure-user";
import { getIcConfig } from "@/lib/network";
import { fireMintConfetti } from "@/lib/mint-confetti";
import { ROUTES } from "@/lib/routes";
import { cn, formatAmountPlain, formatNum, formatUsd, parseAmount } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { BrandTokenMark } from "@/components/brand/BrandTokenMark";
import {
	closeEmptyVault,
	createVault,
	depositCollateral,
	mintUsdm,
} from "@motokomaker/ic-client";
import { Coins } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { hasProtocolEnv } from "@/lib/protocol-actor";

type MintSuccess = {
	vaultId: string;
	mintedUsdm: number;
	collateral: { key: CollateralKey; amount: number; usd: number }[];
	collateralUsd: number;
};

const STEPS = [
	{ number: 1, label: "Deposit" },
	{ number: 2, label: "Mint" },
	{ number: 3, label: "Review" },
];

const KEYS: CollateralKey[] = ["ICP", "ckBTC", "ckETH"];

/** Format a mint amount that never exceeds max after 2-decimal rounding. */
function snapMintInput(value: number, maxMint: number): string {
	if (!(maxMint > 0) || !(value > 0)) return "0";
	let n = Math.min(value, maxMint);
	let s = formatAmountPlain(n, 2);
	while (parseAmount(s) > maxMint + 1e-12 && n > 0) {
		n = Math.max(0, n - 0.01);
		s = formatAmountPlain(n, 2);
	}
	return s;
}

/** Aave/Uniswap-style: Deposit → Mint → Review. Starts on collateral. */
export function BorrowSection() {
	const { balances, refreshBalances, ensureProtocolActor } = useAuth();
	const navigate = useNavigate();
	const qc = useQueryClient();
	const { data: priceData } = useGetPublicPriceFeeds();
	const { data: riskData } = useProtocolRiskParams();
	const riskParams = toRiskParams(riskData);
	const minCr = riskParams.minCollateralRatio;
	const liqT = riskParams.liquidationThreshold;
	const maxMintLtv = maxLtvFromCr(minCr);
	const [step, setStep] = useState(1);
	const [amounts, setAmounts] = useState<Record<CollateralKey, string>>({
		ICP: "",
		ckBTC: "",
		ckETH: "",
	});
	const [mintAmount, setMintAmount] = useState("");
	const [stage, setStage] = useState<UpdateStage>("idle");
	const [submitting, setSubmitting] = useState(false);
	const [success, setSuccess] = useState<MintSuccess | null>(null);

	const prices = priceData?.prices ?? FIXTURE.prices;

	const deposits = useMemo(() => {
		return KEYS.map((k) => {
			const n = parseAmount(amounts[k]);
			const rawBal =
				k === "ICP"
					? balances.ICP
					: k === "ckBTC"
						? balances.ckBTC
						: balances.ckETH;
			const bal = depositableTokenMax(rawBal);
			const usd = n * (prices[k] ?? 0);
			return { key: k, amount: n, bal, usd, price: prices[k] ?? 0 };
		}).filter((d) => d.amount > 0);
	}, [amounts, balances, prices]);

	const colUsd = deposits.reduce((s, d) => s + d.usd, 0);
	const maxMint = mintableUsdmMax(colUsd, 0, minCr);
	const mintRaw = parseAmount(mintAmount);
	/** Always clamp UI mint to fee/haircut-safe max so slider 100% never exceeds loan. */
	const mint =
		maxMint > 0 ? Math.min(mintRaw, maxMint) : mintRaw > 0 ? mintRaw : 0;
	const ltvPct = colUsd > 0 ? (mint / colUsd) * 100 : 0;
	// Health ≡ CR = coll/debt (protocol healthFactorE8)
	const cr =
		mint > 0 && colUsd > 0 ? colUsd / mint : Number.POSITIVE_INFINITY;
	const hf = cr;

	/** Dominant asset for liq price display */
	const dominant = useMemo(() => {
		if (deposits.length === 0) return null;
		return [...deposits].sort((a, b) => b.usd - a.usd)[0]!;
	}, [deposits]);

	const liqPrice =
		dominant && mint > 0
			? liquidationPrice(mint, dominant.amount, liqT)
			: 0;

	const overBalance = deposits.some((d) => d.amount > d.bal + 1e-12);

	function setMintClamped(value: number) {
		setMintAmount(snapMintInput(value, maxMint));
	}

	const balFor = (k: CollateralKey) =>
		depositableTokenMax(
			k === "ICP" ? balances.ICP : k === "ckBTC" ? balances.ckBTC : balances.ckETH,
		);

	function openSuccess(payload: MintSuccess) {
		setSuccess(payload);
		if (payload.mintedUsdm > 0) fireMintConfetti();
	}

	async function submit() {
		if (deposits.length === 0 || colUsd <= 0) {
			toast.error("Deposit a valid collateral amount before creating a vault");
			return;
		}
		if (overBalance) {
			toast.error("Amount exceeds spendable wallet balance (after transfer fee)");
			return;
		}
		if (mint > maxMint + 1e-9) {
			toast.error("Mint exceeds max loan size (after protocol haircut)");
			return;
		}
		setSubmitting(true);
		setStage("signing");
		let createdId: bigint | null = null;
		try {
			if (!hasProtocolEnv()) {
				const ok = await runDemoUpdateStages(setStage);
				if (!ok) throw new Error("Demo mint failed");
				setStage("confirmed");
				openSuccess({
					vaultId: "demo",
					mintedUsdm: mint,
					collateral: deposits.map((d) => ({
						key: d.key,
						amount: d.amount,
						usd: d.usd,
					})),
					collateralUsd: colUsd,
				});
				toast.success(
					"Vault created & USDM minted (demo — set canister env for live)",
				);
				return;
			}
			const pair = await ensureProtocolActor();
			if (!pair) {
				throw new Error(
					"Protocol actor not ready — rebuild SPA with protocol env or reconnect after Connect",
				);
			}
			await ensureProtocolUser(pair.actor);
			const config = getIcConfig();
			setStage("submitting");
			const id = await createVault(pair.actor);
			createdId = id;
			try {
				for (const d of deposits) {
					await depositCollateral(pair.actor, pair.agent, config, {
						vaultId: id,
						collateralType: d.key,
						amount: d.amount,
					});
				}
				if (mint > 0) {
					await mintUsdm(pair.actor, id, mint);
				}
			} catch (inner) {
				// Avoid leaving empty vaults on partial failure
				try {
					await closeEmptyVault(pair.actor, id);
				} catch {
					/* may still hold partial coll — user can close from detail */
				}
				throw inner;
			}
			setStage("confirmed");
			openSuccess({
				vaultId: id.toString(),
				mintedUsdm: mint,
				collateral: deposits.map((d) => ({
					key: d.key,
					amount: d.amount,
					usd: d.usd,
				})),
				collateralUsd: colUsd,
			});
			toast.success(
				mint > 0
					? `Minted ${formatNum(mint, 2)} USDM · Vault #${id.toString()}`
					: `Vault #${id.toString()} created with collateral`,
			);
			void refreshBalances();
			void qc.invalidateQueries({ queryKey: ["v2", "userVaults"] });
			void qc.invalidateQueries({ queryKey: ["v2", "public", "vaults"] });
			void qc.invalidateQueries({ queryKey: ["v2", "wallet", "activity"] });
		} catch (e) {
			setStage("idle");
			void createdId;
			toast.error(e instanceof Error ? e.message : "Mint failed");
		} finally {
			setSubmitting(false);
		}
	}

	const summaryCard = (
		<div className="card-surface space-y-3 border-primary/20 p-4">
			<p className="text-[10px] font-semibold uppercase tracking-wider text-primary">
				Loan summary
			</p>
			<div className="flex justify-between text-sm">
				<span className="text-muted-foreground">Collateral</span>
				<span className="font-mono-amount font-bold">{formatUsd(colUsd)}</span>
			</div>
			<div className="flex items-center justify-between text-sm">
				<span className="text-muted-foreground">Max USDM</span>
				<TokenAmount id="USDM" value={maxMint} decimals={2} size="sm" />
			</div>
			{mint > 0 ? (
				<>
					<div className="flex justify-between text-sm">
						<span className="text-muted-foreground">LTV</span>
						<span className="font-mono-amount font-semibold">
							{ltvPct.toFixed(1)}%
						</span>
					</div>
					<div className="flex justify-between text-sm">
						<span className="text-muted-foreground">Liq. CR</span>
						<span className="font-mono-amount font-semibold">
							{liqT.toFixed(2)}×
						</span>
					</div>
				</>
			) : (
				<p className="text-xs text-muted-foreground">
					Deposit collateral to unlock max loan size (CR ≥{" "}
					{minCr.toFixed(2)}×).
				</p>
			)}
		</div>
	);

	return (
		<div className="grid gap-6 lg:grid-cols-[1fr_280px]">
			<div className="card-surface p-4 sm:p-6">
				<StepIndicator
					steps={STEPS}
					current={step}
					onStepClick={(n) => {
						if (n < step) setStep(n);
					}}
				/>

				{/* Step 1 — Deposit */}
				{step === 1 && (
					<div className="mt-5 space-y-4">
						<div>
							<p className="text-sm font-semibold">Deposit collateral</p>
							<p className="text-xs text-muted-foreground">
								Enter amounts from your wallet. Max loan updates live (like Aave
								supply).
							</p>
						</div>

						<ul className="space-y-2">
							{KEYS.map((k) => {
								const bal = balFor(k);
								const n = parseAmount(amounts[k]);
								const over = n > bal + 1e-12;
								return (
									<li
										key={k}
										className={cn(
											"rounded-2xl border p-3 sm:p-4",
											n > 0 ? "border-primary/40 bg-primary/5" : "border-border",
											over && "border-danger/50",
										)}
									>
										<div className="flex flex-wrap items-center gap-3">
											<div className="flex min-w-0 flex-1 items-center gap-2">
												<TokenIcon token={k} size="md" plain />
												<div>
													<p className="text-sm font-semibold">{k}</p>
													<p className="font-mono-amount text-[11px] text-muted-foreground">
														Wallet {formatNum(bal, k === "ckBTC" ? 4 : 2)} · $
														{formatNum(prices[k], k === "ICP" ? 2 : 0)}
													</p>
												</div>
											</div>
											<div className="flex w-full items-center gap-2 sm:w-auto">
												<input
													value={amounts[k]}
													onChange={(e) =>
														setAmounts((a) => ({ ...a, [k]: e.target.value }))
													}
													inputMode="decimal"
													placeholder="0.00"
													className="h-11 min-w-0 flex-1 rounded-xl border border-border bg-background px-3 font-mono-amount text-sm outline-none focus:border-primary sm:w-36 sm:flex-none"
												/>
												<button
													type="button"
													className="min-h-10 rounded-lg px-2 text-[11px] font-semibold text-primary"
													onClick={() => {
														const half = bal / 2;
														setAmounts((a) => ({
															...a,
															[k]: formatAmountPlain(
																half,
																k === "ckBTC" ? 6 : 4,
															),
														}));
													}}
												>
													50%
												</button>
												<button
													type="button"
													className="min-h-10 rounded-lg px-2 text-[11px] font-semibold text-primary"
													onClick={() =>
														setAmounts((a) => ({
															...a,
															[k]: formatAmountPlain(
																bal,
																k === "ckBTC" ? 6 : 4,
															),
														}))
													}
												>
													Max
												</button>
											</div>
										</div>
										{over ? (
											<p className="mt-1 text-[11px] text-danger">
												Exceeds wallet balance
											</p>
										) : n > 0 ? (
											<p className="mt-1 text-[11px] text-muted-foreground">
												≈ {formatUsd(n * prices[k])}
											</p>
										) : null}
									</li>
								);
							})}
						</ul>

						<p className="text-xs text-muted-foreground">
							Opens a <strong className="text-foreground">new vault</strong> on
							confirm. Manage existing positions on{" "}
							<Link to={ROUTES.vaults} className="font-semibold text-primary">
								Vaults
							</Link>
							.
						</p>

						<div className="lg:hidden">{summaryCard}</div>

						{deposits.length === 0 ? (
							<p className="text-xs text-warning">
								Enter at least one collateral amount (max = wallet − fee) to
								open a vault.
							</p>
						) : null}

						<Button
							fullWidth
							disabled={colUsd <= 0 || overBalance || deposits.length === 0}
							onClick={() => {
								if (deposits.length === 0 || colUsd <= 0) {
									toast.error("Deposit collateral first");
									return;
								}
								if (!mintAmount && maxMint > 0) {
									setMintClamped(maxMint * 0.5);
								}
								setStep(2);
							}}
						>
							Continue to mint
						</Button>
					</div>
				)}

				{/* Step 2 — Mint */}
				{step === 2 && (
					<div className="mt-5 space-y-4">
						<div className="flex justify-center">
							<RiskGauge
								ltv={ltvPct}
								healthFactor={hf}
								maxLtv={maxMintLtv}
								liquidationCr={liqT}
								mintCr={minCr}
								size="lg"
								centerMetric="ltv"
							/>
						</div>

						<label className="block text-xs font-semibold text-muted-foreground">
							Mint amount
							<div className="mt-1 flex items-center gap-2">
								<input
									value={mintAmount}
									onChange={(e) => {
										const raw = e.target.value;
										const n = parseAmount(raw);
										if (maxMint > 0 && n > maxMint + 1e-12) {
											setMintClamped(maxMint);
											return;
										}
										setMintAmount(raw);
									}}
									onBlur={() => {
										if (maxMint > 0 && mintRaw > maxMint + 1e-12) {
											setMintClamped(maxMint);
										}
									}}
									inputMode="decimal"
									placeholder="0.00"
									className="h-12 flex-1 rounded-xl border border-border bg-muted/40 px-3 font-mono-amount text-lg outline-none focus:border-primary"
								/>
								<TokenLabel id="USDM" size="md" />
							</div>
						</label>

						<p className="text-xs text-muted-foreground">
							Max mintable{" "}
							<span className="font-mono font-semibold text-foreground">
								{formatNum(maxMint, 2)} USDM
							</span>{" "}
							(after safety haircut · CR ≥ {minCr.toFixed(2)}×)
						</p>

						{maxMint <= 0 ? (
							<p className="text-xs text-warning">
								Add collateral (and live prices) before using the mint slider.
							</p>
						) : null}
						<div className="flex flex-wrap gap-2">
							<button
								type="button"
								className="text-[11px] font-semibold text-primary"
								disabled={maxMint <= 0}
								onClick={() => setMintClamped(maxMint * 0.5)}
							>
								50%
							</button>
							<button
								type="button"
								className="text-[11px] font-semibold text-primary"
								disabled={maxMint <= 0}
								onClick={() => setMintClamped(maxMint)}
							>
								Max
							</button>
						</div>
						<RangeSlider
							id="mint-usdm-pct"
							label="Percent of max loan"
							valueLabel={
								maxMint > 0
									? `${Math.min(100, (mint / maxMint) * 100).toFixed(1)}%`
									: "0%"
							}
							min={0}
							max={100}
							step={0.1}
							disabled={maxMint <= 0}
							value={
								maxMint > 0
									? Math.min(100, Math.max(0, (mint / maxMint) * 100))
									: 0
							}
							onChange={(pct) => {
								if (maxMint <= 0) {
									setMintAmount("0");
									return;
								}
								const capped = Math.min(100, Math.max(0, pct));
								setMintClamped((maxMint * capped) / 100);
							}}
						/>

						{dominant && mint > 0 ? (
							<p className="text-xs text-muted-foreground">
								Est. liq. price ({dominant.key}):{" "}
								<span className="font-mono-amount font-semibold text-foreground">
									${formatNum(liqPrice, dominant.key === "ICP" ? 2 : 0)}
								</span>{" "}
								· Liq. CR {liqT.toFixed(2)}×
							</p>
						) : null}

						<p className="text-xs text-muted-foreground">
							APR {FIXTURE.borrowApr}% · Max{" "}
							<TokenAmount id="USDM" value={maxMint} decimals={2} size="sm" />
						</p>

						<div className="lg:hidden">{summaryCard}</div>

						<div className="flex gap-2">
							<Button variant="secondary" fullWidth onClick={() => setStep(1)}>
								Back
							</Button>
							<Button
								fullWidth
								disabled={mint <= 0}
								onClick={() => setStep(3)}
							>
								Review
							</Button>
						</div>
					</div>
				)}

				{/* Step 3 — Review */}
				{step === 3 && (
					<div className="mt-5 space-y-4">
						<p className="text-sm font-semibold">Review & confirm</p>
						<p className="text-xs text-muted-foreground">
							Creates vault if needed, deposits collateral, then mints USDM
							(update calls).
						</p>

						<dl className="space-y-2.5 rounded-2xl border border-border p-4 text-sm">
							<div className="flex justify-between">
								<dt className="text-muted-foreground">Vault</dt>
								<dd className="font-mono-amount">
									New multi-collateral vault
								</dd>
							</div>
							{deposits.map((d) => (
								<div
									key={d.key}
									className="flex items-center justify-between gap-2"
								>
									<dt className="text-muted-foreground">Deposit {d.key}</dt>
									<dd>
										<TokenAmount
											id={d.key}
											value={d.amount}
											decimals={d.key === "ckBTC" ? 4 : 2}
											size="sm"
										/>
									</dd>
								</div>
							))}
							<div className="flex justify-between border-t border-border pt-2">
								<dt className="text-muted-foreground">Total collateral</dt>
								<dd className="font-mono-amount font-bold">
									{formatUsd(colUsd)}
								</dd>
							</div>
							<div className="flex items-center justify-between">
								<dt className="text-muted-foreground">Mint USDM</dt>
								<dd>
									<TokenAmount id="USDM" value={mint} decimals={2} size="sm" />
								</dd>
							</div>
							<div className="flex justify-between">
								<dt className="text-muted-foreground">LTV</dt>
								<dd className="font-mono-amount">{ltvPct.toFixed(2)}%</dd>
							</div>
							<div className="flex justify-between">
								<dt className="text-muted-foreground">Health factor</dt>
								<dd className="font-mono-amount">
									{Number.isFinite(hf) ? `${hf.toFixed(2)}×` : "∞"}
								</dd>
							</div>
							<div className="flex justify-between">
								<dt className="text-muted-foreground">Collateralization</dt>
								<dd className="font-mono-amount">
									{Number.isFinite(cr) ? `${cr.toFixed(2)}×` : "∞"}
								</dd>
							</div>
							<div className="flex justify-between">
								<dt className="font-semibold text-foreground">
									Liquidation CR
								</dt>
								<dd className="font-mono-amount font-bold">
									{liqT.toFixed(2)}×
								</dd>
							</div>
							{dominant && mint > 0 ? (
								<div className="flex justify-between">
									<dt className="font-semibold text-foreground">
										Liq. price ({dominant.key})
									</dt>
									<dd className="font-mono-amount font-bold">
										${formatNum(liqPrice, dominant.key === "ICP" ? 2 : 0)}
									</dd>
								</div>
							) : null}
							<div className="flex justify-between">
								<dt className="text-muted-foreground">Stability fee (APR)</dt>
								<dd className="font-mono-amount">{FIXTURE.borrowApr}%</dd>
							</div>
						</dl>

						{stage !== "idle" && <UpdateStageLoader stage={stage} />}

						<div className="flex gap-2">
							<Button variant="secondary" fullWidth onClick={() => setStep(2)}>
								Back
							</Button>
							<RequireAuthAction
								labelWhenUnauthed="Connect to mint USDM"
								onAuthenticated={() => void submit()}
								disabled={
									submitting ||
									overBalance ||
									mint <= 0 ||
									deposits.length === 0 ||
									colUsd <= 0
								}
								iconLeft={<Coins className="size-4" />}
							>
								{submitting ? "Confirming…" : "Confirm mint USDM"}
							</RequireAuthAction>
						</div>
					</div>
				)}
			</div>

			{/* Desktop sticky summary */}
			<aside className="hidden lg:block">
				<div className="sticky top-36 space-y-3">{summaryCard}</div>
			</aside>

			{/* Post-mint operations summary + next steps */}
			<ConfirmSheet
				open={success !== null}
				onOpenChange={(o) => {
					if (!o) setSuccess(null);
				}}
				title={
					success && success.mintedUsdm > 0
						? "USDM minted"
						: "Vault created"
				}
				titleIcon={
					success && success.mintedUsdm > 0 ? (
						<BrandTokenMark token="USDM" size="md" />
					) : undefined
				}
				description={
					success
						? `Vault #${success.vaultId} is on-chain. Review the operation details, then choose a next step.`
						: ""
				}
				confirmLabel="Close"
				onConfirm={() => setSuccess(null)}
				hideFooter
			>
				{success ? (
					<div className="space-y-4">
						<div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
							<p className="text-[10px] font-semibold uppercase tracking-wider text-primary">
								Operation details
							</p>
							<ul className="mt-3 space-y-2 text-sm">
								{success.collateral.map((c) => (
									<li
										key={c.key}
										className="flex items-center justify-between gap-2"
									>
										<span className="flex items-center gap-2 text-muted-foreground">
											<TokenIcon token={c.key} size="sm" plain />
											Collateral {c.key}
										</span>
										<span className="font-mono-amount font-semibold">
											{formatNum(
												c.amount,
												c.key === "ckBTC" ? 6 : 4,
											)}{" "}
											· {formatUsd(c.usd)}
										</span>
									</li>
								))}
								<li className="flex items-center justify-between gap-2 border-t border-border/50 pt-2">
									<span className="text-muted-foreground">
										Total collateral
									</span>
									<span className="font-mono-amount font-bold">
										{formatUsd(success.collateralUsd)}
									</span>
								</li>
								<li className="flex items-center justify-between gap-2">
									<span className="text-muted-foreground">Amount</span>
									<TokenAmount
										id="USDM"
										value={success.mintedUsdm}
										decimals={2}
										size="sm"
									/>
								</li>
							</ul>
						</div>

						<div className="grid gap-2">
							<button
								type="button"
								className="btn-primary flex w-full items-center justify-center gap-2 py-3 text-sm"
								onClick={() => {
									setSuccess(null);
									navigate(ROUTES.wallet);
								}}
							>
								Go to Wallet
							</button>
							<button
								type="button"
								className="btn-secondary flex w-full items-center justify-center gap-2 py-3 text-sm"
								onClick={() => {
									setSuccess(null);
									navigate(ROUTES.governance);
								}}
							>
								<BrandTokenMark token="MMK" size="sm" />
								Buy MotokoMaker governance tokens
							</button>
							<button
								type="button"
								className="btn-secondary flex w-full items-center justify-center gap-2 py-3 text-sm"
								onClick={() => {
									setSuccess(null);
									navigate(ROUTES.savings);
								}}
							>
								<BrandTokenMark token="USDM" size="sm" />
								Add MotokoDollars to savings
							</button>
							{success.vaultId !== "demo" ? (
								<button
									type="button"
									className="text-center text-xs font-semibold text-primary underline-offset-2 hover:underline"
									onClick={() => {
										const id = success.vaultId;
										setSuccess(null);
										navigate(ROUTES.vaultDetail(id));
									}}
								>
									Open vault #{success.vaultId}
								</button>
							) : null}
						</div>
					</div>
				) : null}
			</ConfirmSheet>
		</div>
	);
}
